import { jsx as _jsx } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-05-30 16:47:47 +0200 (Sat, 30 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163414 $
//  Purpose   : image viewer low level image control
// =============================================================================
// ---Third-party imports
import React from 'react';
import clsx from 'clsx';
import { Draggable } from 'uix/chunks';
// ---Framework imports
import { useRef, useSelector, useSelectorShallow, useEffect, useActions, useMemo, useState, useTestableState } from 'uix/hooks';
// ---Local imports
import { requestViewerImage, releaseViewerImage } from './actions';
import styles from '../viewer.css';
// usable as img/@src
const black64_96 = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=';
const transparent64 = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
let nextConsumerId = 1;
let getNextConsumerId = ()=>`viewerimg_${nextConsumerId++}`;
let getViewerImageIdentity = (src)=>{
    if (!src) {
        return null;
    }
    let tiler = src.match(/\/image\/tiler\/([^/]+)\/[^/]+\/([^/]+)\/([^/?#]+)/);
    if (tiler) {
        let [, id, i, j] = tiler;
        return `tiler:${id}:${i}:${j}`;
    }
    let webcache = src.match(/\/download\/webcache\/[^/]+\/([^/?#]+)/);
    if (webcache) {
        let [, id] = webcache;
        return `webcache:${id}`;
    }
    return src;
};
let getViewerImageRef = (src)=>{
    if (!src) {
        return '?';
    }
    let webcache = src.match(/\/download\/webcache\/[^/]+\/([^/?#]+)/);
    if (webcache) {
        return webcache[1];
    }
    let tiler = src.match(/\/image\/tiler\/([^/]+)\/[^/]+\/([^/]+)\/([^/?#]+)/);
    if (tiler) {
        let [, id, i, j] = tiler;
        return `${id}:${i},${j}`;
    }
    return src.split('/').at(-1) || '?';
};
let getViewerImageTestStateId = (viewerId, src, field)=>{
    let ref = encodeURIComponent(src || '?');
    return `ControlledImage:${field}:${viewerId}:${ref}`;
};
let getViewerImageErrorLabel = (error)=>{
    let status = String(error || '').match(/\b(4\d\d|5\d\d)\b/)?.[1];
    return status || 'ERR';
};
let getViewerImageStatusFromPerformanceEntries = (src)=>{
    if (!src || typeof performance == 'undefined' || !performance.getEntriesByName) {
        return null;
    }
    let entry = performance.getEntriesByName(src).filter((item)=>item?.entryType == 'resource').filter((item)=>item?.initiatorType == 'img').at(-1);
    let status = entry?.responseStatus;
    return status >= 400 ? String(status) : null;
};
let viewerImageStatusSubscribers = {};
let viewerImageStatusObserver = null;
let notifyViewerImageStatusSubscribers = (src, status)=>{
    if (!status || !viewerImageStatusSubscribers[src]) {
        return;
    }
    for (let callback of viewerImageStatusSubscribers[src]){
        callback(status);
    }
};
let handleViewerImageTimingEntry = (entry)=>{
    if (entry?.entryType != 'resource' || entry?.initiatorType != 'img') {
        return;
    }
    let status = entry?.responseStatus;
    if (status >= 400) {
        notifyViewerImageStatusSubscribers(entry.name, String(status));
    }
};
let ensureViewerImageStatusObserver = ()=>{
    if (viewerImageStatusObserver || typeof PerformanceObserver == 'undefined') {
        return;
    }
    viewerImageStatusObserver = new PerformanceObserver((list)=>{
        for (let entry of list.getEntries()){
            handleViewerImageTimingEntry(entry);
        }
    });
    viewerImageStatusObserver.observe({
        entryTypes: [
            'resource'
        ]
    });
};
let subscribeViewerImageStatus = (src, callback)=>{
    if (!src) {
        return ()=>{};
    }
    viewerImageStatusSubscribers[src] = viewerImageStatusSubscribers[src] || new Set();
    viewerImageStatusSubscribers[src].add(callback);
    let knownStatus = getViewerImageStatusFromPerformanceEntries(src);
    if (knownStatus) {
        callback(knownStatus);
    }
    ensureViewerImageStatusObserver();
    return ()=>{
        viewerImageStatusSubscribers[src]?.delete(callback);
        if (!viewerImageStatusSubscribers[src]?.size) {
            delete viewerImageStatusSubscribers[src];
        }
    };
};
let buildBrokenImagePlaceholder = ({ src, error, label })=>{
    let ref = getViewerImageRef(src);
    label = label || (getViewerImageErrorLabel(error) == 'ERR' ? getViewerImageStatusFromPerformanceEntries(src) || 'ERR' : getViewerImageErrorLabel(error));
    let svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 320">
      <rect width="240" height="320" rx="14" fill="#f3ede0"/>
      <rect x="10" y="10" width="220" height="300" rx="10" fill="#fbf8f1" stroke="#d2c5ac" stroke-width="2"/>
      <rect x="34" y="112" width="72" height="30" rx="15" fill="#8a5a44"/>
      <text x="70" y="132" text-anchor="middle" font-family="monospace" font-size="16" font-weight="700" fill="#fff">${label}</text>
      <text x="34" y="190" font-family="monospace" font-size="13" fill="#7a6852">id: ${ref}</text>
    </svg>
  `;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};
let buildEmptyImagePlaceholder = ()=>{
    let svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 320">
      <rect width="240" height="320" rx="14" fill="#ececec"/>
      <rect x="10" y="10" width="220" height="300" rx="10" fill="#f8f8f8" stroke="#c8c8c8" stroke-width="2"/>
      <rect x="34" y="112" width="96" height="30" rx="15" fill="#777"/>
      <text x="82" y="132" text-anchor="middle" font-family="monospace" font-size="16" font-weight="700" fill="#fff">NO IMG</text>
      <text x="34" y="190" font-family="monospace" font-size="13" fill="#777">id: ?</text>
    </svg>
  `;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};
// XXX: grow this a dname
// XXX: fix footers
export const wcUrl = (apiurl, wcSize, otid)=>`${apiurl}/download/webcache/${wcSize}/${otid}?orig=true`;
export const ControlledImage = ({ src, viewerId = 'viewer', priority = 0, style, className, ...props })=>{
    let consumerIdRef = useRef(null);
    if (!consumerIdRef.current) {
        consumerIdRef.current = getNextConsumerId();
    }
    let prevSrcRef = useRef(null);
    let prevRequestRef = useRef(null);
    let [drawnSrc, setDrawnSrc] = useTestableState(null, ()=>getViewerImageTestStateId(viewerId, src, 'drawnSrc'));
    let [brokenStatus, setBrokenStatus] = useTestableState(null, ()=>getViewerImageTestStateId(viewerId, src, 'brokenStatus'));
    let entry = useSelector((root)=>src ? root.data.viewer.imageRequests[src] : null);
    let [requestImage, releaseImage] = useActions([
        requestViewerImage,
        releaseViewerImage
    ]);
    let requestImageRef = useRef(requestImage);
    let releaseImageRef = useRef(releaseImage);
    requestImageRef.current = requestImage;
    releaseImageRef.current = releaseImage;
    useEffect(()=>{
        let consumerId = consumerIdRef.current;
        let prevSrc = prevSrcRef.current;
        let nextRequest = src ? `${src}__${viewerId}__${priority}` : null;
        if (prevRequestRef.current == nextRequest) {
            return;
        }
        if (prevSrc && prevSrc !== src) {
            releaseImageRef.current(prevSrc, consumerId);
        }
        if (src) {
            requestImageRef.current(src, consumerId, viewerId, priority);
            prevSrcRef.current = src;
            prevRequestRef.current = nextRequest;
        } else {
            prevSrcRef.current = null;
            prevRequestRef.current = null;
        }
    }, [
        priority,
        src,
        viewerId
    ]);
    useEffect(()=>{
        let consumerId = consumerIdRef.current;
        return ()=>{
            let activeSrc = prevSrcRef.current;
            if (activeSrc) {
                releaseImageRef.current(activeSrc, consumerId);
            }
            prevRequestRef.current = null;
        };
    }, []);
    let state = src ? entry?.state || 'queued' : 'empty';
    let currentIdentity = getViewerImageIdentity(src);
    let drawnIdentity = getViewerImageIdentity(drawnSrc);
    let canReuseDrawnSrc = drawnSrc && currentIdentity && drawnIdentity == currentIdentity;
    let errorLabel = getViewerImageErrorLabel(entry?.error);
    let brokenPlaceholder = buildBrokenImagePlaceholder({
        src,
        error: entry?.error,
        label: brokenStatus
    });
    let emptyPlaceholder = buildEmptyImagePlaceholder();
    useEffect(()=>{
        if (!src && drawnSrc) {
            setDrawnSrc(null);
        } else if (state == 'loaded' && drawnSrc !== src) {
            setDrawnSrc(src);
        }
    }, [
        drawnSrc,
        src,
        state
    ]);
    useEffect(()=>{
        if (state != 'broken') {
            setBrokenStatus(null);
            return;
        }
        if (errorLabel != 'ERR') {
            setBrokenStatus(errorLabel);
            return;
        }
        return subscribeViewerImageStatus(src, setBrokenStatus);
    }, [
        errorLabel,
        src,
        state
    ]);
    let renderedSrc = state == 'empty' ? emptyPlaceholder : state == 'loaded' ? src : state == 'broken' ? canReuseDrawnSrc ? drawnSrc : brokenPlaceholder : canReuseDrawnSrc ? drawnSrc : transparent64;
    style = {
        ...style,
        opacity: renderedSrc == transparent64 ? 0 : 1,
        transition: 'opacity 0.2s',
        backgroundColor: state == 'broken' ? '#FF00000F' : undefined
    };
    return /*#__PURE__*/ _jsx("img", {
        ...props,
        src: renderedSrc,
        style: style,
        className: clsx(styles.ctrlimg, className, state)
    });
};
export const ControlledImageDebug = ({ pages, onClick })=>{
    let { apiurl, imageRequests, wcSizes } = useSelectorShallow((root)=>({
            apiurl: root.config.apiurl,
            imageRequests: root.data.viewer.imageRequests,
            wcSizes: root.config.instance.webcache.sizes
        }));
    let color = (otid, wcSize, orgWidth)=>{
        let url = wcUrl(apiurl, wcSize, otid);
        if (wcSize > orgWidth) {
            return '#EEE';
        }
        switch(imageRequests[url]?.state){
            case 'queued':
                return 'blue';
            case 'loading':
                return 'yellow';
            case 'broken':
                return 'red';
            case 'loaded':
                return 'green';
            default:
                return '';
        }
    };
    let [pixels, setPixels] = useState(7);
    let pw = Math.max(4, pixels) + 3;
    let items = useMemo(()=>pages.map((page)=>{
            let sizes = wcSizes.map((w)=>/*#__PURE__*/ _jsx("span", {
                    title: `${page.id}/${w}`,
                    onClick: ()=>onClick({
                            otid: page.id,
                            zoom: (w === 0 ? page.size[0] : w) - 1
                        }),
                    style: {
                        display: 'inline-block',
                        width: `${pw}px`,
                        height: `${pixels}px`,
                        backgroundColor: color(page.id, w, page.size[0])
                    },
                    children: " "
                }, w));
            return /*#__PURE__*/ _jsx("div", {
                style: {
                    height: `${pixels}px`,
                    backgroundColor: 'white',
                    cursor: 'default'
                },
                children: sizes
            }, page.id);
        }), [
        pages,
        imageRequests,
        pixels
    ]);
    return /*#__PURE__*/ _jsx(Draggable, {
        handle: ".handle",
        children: /*#__PURE__*/ _jsx("div", {
            className: "handle",
            onWheel: (e)=>setPixels(Math.max(2, pixels) - Math.sign(e.deltaY)),
            style: {
                zIndex: 1000,
                border: '1px solid #EEE',
                position: 'absolute',
                top: '2px',
                left: '2%',
                width: `${wcSizes.length * pw + 2}px`
            },
            children: items
        })
    });
};
